import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { ExpenseList, ExpenseForm, ExistingPaymentsList } from 'components/cost';
import { useMeetingMembers } from 'hooks/useMeeting';
import { usePaymentList } from 'hooks/usePayment';
import { meetingStore } from 'store/meetingStore';
import { paymentStore } from 'store/paymentStore';
import { PaymentRepository } from 'repository/paymentRepository';
import { useAlert } from 'contexts/AlertContext';
import './CostInput.css';

interface ExpenseItem {
    id: number;
    description: string;
    amount: number;
    paidBy: string;
    splitCount: number;
}

interface Participant {
    id: string;
    name: string;
}

export const CostInput = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { data: membersData } = useMeetingMembers();
    const { data: paymentData } = usePaymentList();
    const { showAlert } = useAlert();

    const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [paidBy, setPaidBy] = useState('');
    const [splitAmong, setSplitAmong] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingMembers(meetingId);
            paymentStore.onGetPaymentList(meetingId);
        }
    }, [meetingId]);

    const existingPayments = paymentData?.data || [];

    const participants: Participant[] = membersData?.data?.map((member: any) => ({
        id: member.userId,
        name: (member.nickname && member.nickname.trim()) ? member.nickname : (member.userId || '사용자')
    })) || [];

    useEffect(() => {
        if (participants.length > 0 && !paidBy) {
            setPaidBy(participants[0].id);
            setSplitAmong(participants.map((p: Participant) => p.id));
        }
    }, [membersData, paidBy]);

    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    const existingTotalAmount = existingPayments.reduce((sum: number, payment: any) => sum + (payment.totalAmount || 0), 0);

    const handleAddExpense = async () => {
        if (!description.trim() || !amount || splitAmong.length === 0 || !meetingId) return;
        if (isLoading) return;

        setIsLoading(true);

        try {
            const payer = participants.find((p: Participant) => p.id === paidBy);
            const amountNumber = parseInt(amount.replace(/,/g, ''));

            const response = await PaymentRepository.onPostCreatePayment(meetingId, {
                meetingId: meetingId,
                storeName: description.trim(),
                totalAmount: amountNumber,
                splitType: 'EQUAL',
                paidBy: paidBy,
                splitAmong: splitAmong
            });

            if (response?.status === 200 || response?.status === 201) {
                setExpenses([
                    ...expenses,
                    {
                        id: Date.now(),
                        description: description.trim(),
                        amount: amountNumber,
                        paidBy: payer?.name || paidBy,
                        splitCount: splitAmong.length
                    }
                ]);
                setDescription('');
                setAmount('');
                setSplitAmong(participants.map((p: Participant) => p.id));
                showAlert({ message: '지출이 추가되었습니다.' });
            } else {
                showAlert({ message: '지출 추가에 실패했습니다.' });
            }
        } catch (error) {
            console.error('지출 추가 실패:', error);
            showAlert({ message: '지출 추가 중 오류가 발생했습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSplitParticipant = (participantId: string) => {
        setSplitAmong(prev =>
            prev.includes(participantId)
                ? prev.filter(p => p !== participantId)
                : [...prev, participantId]
        );
    };

    const handleRemoveExpense = (id: number) => {
        setExpenses(expenses.filter(expense => expense.id !== id));
    };

    const handleViewResult = () => {
        navigate(`/cost/result/${meetingId}`);
    };

    const handleReceiptClick = () => {
        navigate(`/cost/receipt/${meetingId}`);
    };

    return (
        <div className="cost-input-wrapper">
            <Header title="나의 지출내역 입력" isSubPage />

            <div className="cost-input-content">
                <div className="cost-title-section">
                    <h1 className="cost-title">지출 내역을 입력해주세요</h1>
                    <p className="cost-subtitle">
                        누가 얼마를 지불했는지 기록해보세요
                    </p>
                </div>

                <ExpenseList
                    expenses={expenses}
                    totalAmount={totalAmount}
                    onRemove={handleRemoveExpense}
                />

                <ExpenseForm
                    description={description}
                    amount={amount}
                    paidBy={paidBy}
                    splitAmong={splitAmong}
                    participants={participants}
                    isLoading={isLoading}
                    onDescriptionChange={setDescription}
                    onAmountChange={setAmount}
                    onPaidByChange={setPaidBy}
                    onToggleSplitParticipant={toggleSplitParticipant}
                    onAddExpense={handleAddExpense}
                    onReceiptClick={handleReceiptClick}
                />

                <ExistingPaymentsList
                    payments={existingPayments}
                    totalAmount={existingTotalAmount}
                    onViewResult={handleViewResult}
                />
            </div>
        </div>
    );
};
